import React, { useState } from 'react';
import { 
  Send, 
  Film, 
  User, 
  Sparkles, 
  CheckCircle2, 
  Clapperboard, 
  Video,
  Star
} from 'lucide-react';

export default function Waitlist() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Filmmaker',
    portfolio: '',
    interests: ''
  });

 const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const response = await fetch(
      'https://website.backend.tekvancesolutions.co.ke/api/mbogi-capital/waiting-list',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          primaryRole: formData.role,
          portfolioLink: formData.portfolio,
          message: formData.interests
        })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to submit');
    }

    console.log('✅ Submitted:', result);
    setSubmitted(true);

  } catch (error) {
    console.error('❌ Submission error:', error.message);
    alert(error.message);
  }
};

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold mb-4">You're on the list!</h2>
          <p className="text-slate-500 mb-8">
            Thanks for joining the Mbogi. We'll reach out as soon as we're ready to onboard your talent.
          </p>
          <button 
            onClick={() => setSubmitted(false)}
            className="text-[#c07d3e] font-bold hover:underline"
          >
            Submit another response
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans text-slate-900">
      
      {/* --- HEADER --- */}
      <section className="pt-20 pb-12 px-6 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#c07d3e] px-4 py-1.5 rounded-full mb-6">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-widest">Join the Movement</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
          Own Your Narrative.<br />
          <span className="text-[#c07d3e]">Join the Waitlist.</span>
        </h1>
        <p className="text-slate-500 text-lg md:text-xl leading-relaxed">
          Whether you're a director, a DP, or an avid fan of African cinema, 
          MbogiCapital is built for you. Secure your spot for early access to the ecosystem.
        </p>
      </section>

      {/* --- FORM SECTION --- */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto bg-slate-50 border border-slate-100 rounded-[3rem] p-8 md:p-12 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Identity Group */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input 
                    type="text" 
                    required
                    placeholder="Enter your name"
                    className="w-full pl-12 pr-5 py-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Email Address</label>
                <input 
                  type="email" 
                  required
                  placeholder="name@email.com"
                  className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Primary Role</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Filmmaker', 'Crew', 'Talent', 'Fan'].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setFormData({...formData, role: r})}
                    className={`py-3 px-4 rounded-xl text-sm font-bold border transition-all ${
                      formData.role === r 
                      ? 'bg-[#c07d3e] border-[#c07d3e] text-white' 
                      : 'bg-white border-slate-200 text-slate-500 hover:border-orange-200'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Portfolio / Link */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Portfolio or Social Link (Optional)</label>
              <div className="relative">
                <Video className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input 
                  type="url" 
                  placeholder="https://vimeo.com/..."
                  className="w-full pl-12 pr-5 py-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all"
                  onChange={(e) => setFormData({...formData, portfolio: e.target.value})}
                />
              </div>
            </div>

            {/* Why Join? */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">What brings you to the Mbogi?</label>
              <textarea 
                rows="4"
                placeholder="Tell us about your passion for African film..."
                className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#c07d3e] transition-all resize-none"
                onChange={(e) => setFormData({...formData, interests: e.target.value})}
              ></textarea>
            </div>

            {/* Submit */}
            <button 
              type="submit" 
              className="w-full bg-[#0a1d2e] hover:bg-slate-800 text-white py-5 rounded-2xl font-extrabold flex items-center justify-center gap-3 transition-all group"
            >
              Get Early Access <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </form>
        </div>
      </section>

      {/* --- QUICK STATS / TRUST --- */}
      <section className="py-12 px-6 border-t border-slate-100">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8 opacity-60">
          <div className="flex items-center gap-3">
            <Clapperboard className="w-6 h-6" />
            <span className="font-bold text-sm uppercase tracking-widest">15+ Partner Studios</span>
          </div>
          <div className="flex items-center gap-3">
            <Film className="w-6 h-6" />
            <span className="font-bold text-sm uppercase tracking-widest">Authentic Stories</span>
          </div>
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6" />
            <span className="font-bold text-sm uppercase tracking-widest">Community Owned</span>
          </div>
        </div>
      </section>

    </div>
  );
}